import React, { useEffect, useMemo, useState,useCallback } from "react";
import { db } from "../firebase/firebaseConfig";
import { getDocs, collection, where, query } from "firebase/firestore";
import { useContext } from "react";
import { UserContext } from "../context/UserContext";

import {
  Segment,
  Table,
  Container,
  Grid,
  Header,
  Icon,
  Label,
  Input,
  Button,
  Dropdown,
  Message,
} from "semantic-ui-react";
import { Link } from "react-router-dom";
import moment from "moment";

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f9f9f9",
};

const RANGE = {
  ALL: 0,
  D7: 7,
  D30: 30,
  D90: 90,
};

const Bitacoras = () => {
  const { users } = useContext(UserContext);

  const [bitacoras, setBitacoras] = useState([]);
  const [loading, setLoading] = useState(false);

  // filtros UI
  const [busqueda, setBusqueda] = useState("");
  const [rango, setRango] = useState(RANGE.D90);

  const getBitacoras = useCallback(async () => {
    if (!users?.emp_id) return;

    setLoading(true);
    try {
      const traerCabecera = collection(db, "bitacoracab");
      const qCab = query(traerCabecera, where("emp_id", "==", users.emp_id));
      const snap = await getDocs(qCab);
      setBitacoras(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } finally {
      setLoading(false);
    }
  }, [users?.emp_id]);

  useEffect(() => {
    getBitacoras();
  }, [getBitacoras]);

  const formatearFecha = (fecha) => {
    if (!fecha?.toDate) return "-";
    return moment(fecha.toDate()).format("DD/MM/YYYY HH:mm");
  };

  // ordenar por fecha mantención DESC (más reciente arriba)
  const ordenadas = useMemo(() => {
    return [...bitacoras].sort((a, b) => {
      const aMs = a?.fecha_mantencion?.toMillis?.() ?? 0;
      const bMs = b?.fecha_mantencion?.toMillis?.() ?? 0;
      return bMs - aMs;
    });
  }, [bitacoras]);

  // rango tiempo (últimos N días)
  const rangoMs = useMemo(() => {
    if (!rango || rango === RANGE.ALL) {
      return { fromMs: Number.NEGATIVE_INFINITY, toMs: Number.POSITIVE_INFINITY };
    }
    const toMs = moment().endOf("day").valueOf();
    const fromMs = moment().subtract(rango, "days").startOf("day").valueOf();
    return { fromMs, toMs };
  }, [rango]);

  // filtrar por rango + búsqueda
  const filtradas = useMemo(() => {
    const q = (busqueda || "").trim().toLowerCase();

    const porFecha = ordenadas.filter((b) => {
      const ms = b?.fecha_mantencion?.toMillis?.() ?? 0;
      return ms >= rangoMs.fromMs && ms <= rangoMs.toMs;
    });

    if (!q) return porFecha;

    return porFecha.filter((b) => {
      const hay = [
        b.tipo,
        b.serie,
        b.nombre_protocolo,
        b.programa,
        b.familia,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [ordenadas, busqueda, rangoMs]);

  const opcionesRango = [
    { key: "D7", text: "Últimos 7 días", value: RANGE.D7, icon: "calendar alternate outline" },
    { key: "D30", text: "Últimos 30 días", value: RANGE.D30, icon: "calendar alternate outline" },
    { key: "D90", text: "Últimos 90 días", value: RANGE.D90, icon: "calendar alternate outline" },
    { key: "ALL", text: "Todo", value: RANGE.ALL, icon: "list" },
  ];

  const limpiarFiltros = () => {
    setBusqueda("");
    setRango(RANGE.D90);
  };

  return (
    <Container fluid style={{ maxWidth: 1400 }}>
      {/* CABECERA */}
      <Segment inverted color="teal" tertiary>
        <Header as="h2" icon textAlign="center">
          <Icon name="book" />
          Bitácoras
          <Header.Subheader>Historial de mantenciones y acceso al checklist PDF</Header.Subheader>
        </Header>
      </Segment>

      {/* CONTROLES */}
      <Segment>
        <Grid stackable verticalAlign="middle">
          <Grid.Row columns={4}>
            <Grid.Column width={7}>
              <Input
                fluid
                icon="search"
                placeholder="Buscar por serie, equipo, protocolo, programa…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </Grid.Column>

            <Grid.Column width={5}>
              <Dropdown
                fluid
                selection
                options={opcionesRango}
                value={rango}
                onChange={(_, data) => setRango(data.value)}
                placeholder="Rango"
              />
            </Grid.Column>

            <Grid.Column width={2} textAlign="center">
              <Label color="teal" size="large">
                Resultados <Label.Detail>{filtradas.length}</Label.Detail>
              </Label>
            </Grid.Column>

            <Grid.Column width={2} textAlign="right">
              <Button basic icon labelPosition="left" onClick={getBitacoras} loading={loading}>
                <Icon name="refresh" />
                Actualizar
              </Button>
              <Button basic icon labelPosition="left" onClick={limpiarFiltros} style={{ marginLeft: 8 }}>
                <Icon name="eraser" />
                Limpiar
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      {/* TABLA */}
      <Segment raised color="teal" style={{ padding: 0 }}>
        <div style={{ maxHeight: 520, overflow: "auto", padding: 10 }}>
          <Table celled compact striped selectable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={stickyTh}>N°</Table.HeaderCell>
                <Table.HeaderCell style={stickyTh}>Equipo</Table.HeaderCell>
                <Table.HeaderCell style={stickyTh}>Serie</Table.HeaderCell>
                <Table.HeaderCell style={stickyTh}>Protocolo</Table.HeaderCell>
                <Table.HeaderCell style={stickyTh}>Fecha Mantención</Table.HeaderCell>
                <Table.HeaderCell style={stickyTh} textAlign="center">PDF</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {loading && (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Message icon info style={{ margin: 0 }}>
                      <Icon name="spinner" loading />
                      <Message.Content>
                        <Message.Header>Cargando bitácoras…</Message.Header>
                        Consultando colección bitacoracab.
                      </Message.Content>
                    </Message>
                  </Table.Cell>
                </Table.Row>
              )}

              {!loading &&
                filtradas.map((item, index) => (
                  <Table.Row key={item.id ?? index}>
                    <Table.Cell>{index + 1}</Table.Cell>

                    <Table.Cell style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                      <b>{item.tipo}</b>
                      {item.familia ? (
                        <div style={{ fontSize: 12, opacity: 0.8 }}>
                          Fam: {item.familia}
                        </div>
                      ) : null}
                    </Table.Cell>

                    <Table.Cell>{item.serie}</Table.Cell>

                    <Table.Cell style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                      {item.nombre_protocolo}
                      {item.programa ? (
                        <div style={{ fontSize: 12, opacity: 0.8 }}>
                          Prog: {item.programa}
                        </div>
                      ) : null}
                    </Table.Cell>

                    <Table.Cell>{formatearFecha(item.fecha_mantencion)}</Table.Cell>

                    <Table.Cell textAlign="center">
                      <Button
                        as={Link}
                        to={`/checkmantencion/${item.id}`}
                        size="tiny"
                        color="red"
                        icon
                        labelPosition="left"
                        title="Ver CheckList de Mantención"
                      >
                        <Icon name="file pdf" />
                        PDF
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}

              {!loading && filtradas.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Message warning icon style={{ margin: 0 }}>
                      <Icon name="search" />
                      <Message.Content>
                        <Message.Header>Sin resultados</Message.Header>
                        No hay bitácoras que coincidan con los filtros actuales.
                      </Message.Content>
                    </Message>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </Segment>
    </Container>
  );
};

export default Bitacoras;